import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useParams, Link } from "react-router-dom";
import { post } from "aws-amplify/api";
import { API_NAME } from "../lib/amplify";
import { extractApiError } from "../lib/api_error";
import {
  useAdminUsers,
  fetchAdminUsers,
  assignUser,
  removeUser,
} from "../stores/admin_user_store";
import { useCustomers, fetchCustomers } from "../stores/customer_store";
import type { AppUser } from "../../../shared/models/user";

interface TestDataSummary {
  policiesCreated: number;
  deviceFamiliesCreated: number;
  certificatesCreated: number;
}

export const AdminCustomerDetail: FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const users = useAdminUsers();
  const customers = useCustomers();

  const [error, setError] = useState<string | undefined>();
  const [assigningEmail, setAssigningEmail] = useState<string | undefined>();
  const [removingEmail, setRemovingEmail] = useState<string | undefined>();
  const [testDataLoading, setTestDataLoading] = useState(false);
  const [testDataSummary, setTestDataSummary] = useState<
    TestDataSummary | undefined
  >();
  const [testDataError, setTestDataError] = useState<string | undefined>();

  // Reassignment warning modal state
  const [reassignTarget, setReassignTarget] = useState<AppUser | undefined>();

  const customer = useMemo(
    () => customers?.find((c) => c.customerId === customerId),
    [customers, customerId]
  );

  const assignedUsers = useMemo(
    () => (users ?? []).filter((u) => u.customerId === customerId),
    [users, customerId]
  );

  const availableUsers = useMemo(
    () => (users ?? []).filter((u) => u.customerId !== customerId),
    [users, customerId]
  );

  // Fetch data on mount
  useEffect(() => {
    fetchAdminUsers().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to load users");
    });
    fetchCustomers().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to load customers");
    });
  }, []);

  const handleAssign = useCallback(
    async (user: AppUser) => {
      if (!customerId) return;

      // If user is already assigned to a different customer, show warning
      if (user.customerId !== undefined && user.customerId !== customerId) {
        setReassignTarget(user);
        return;
      }

      setAssigningEmail(user.email);
      setError(undefined);
      try {
        await assignUser(user.userId, customerId);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to assign user");
      } finally {
        setAssigningEmail(undefined);
      }
    },
    [customerId]
  );

  const handleConfirmReassign = useCallback(async () => {
    if (!customerId || !reassignTarget) return;

    setReassignTarget(undefined);
    setAssigningEmail(reassignTarget.email);
    setError(undefined);
    try {
      await assignUser(reassignTarget.userId, customerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reassign user");
    } finally {
      setAssigningEmail(undefined);
    }
  }, [customerId, reassignTarget]);

  const handleRemove = useCallback(async (userId: string) => {
    setRemovingEmail(userId);
    setError(undefined);
    try {
      await removeUser(userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove user");
    } finally {
      setRemovingEmail(undefined);
    }
  }, []);

  const handleGenerateTestData = useCallback(async () => {
    if (!customerId) return;

    setTestDataLoading(true);
    setTestDataError(undefined);
    setTestDataSummary(undefined);
    try {
      const { body } = await post({
        apiName: API_NAME,
        path: "/admin/generate-test-data",
        options: { body: { customerId } },
      }).response;

      const result = (await body.json()) as unknown as {
        customerId: string;
        summary: TestDataSummary;
      };
      setTestDataSummary(result.summary);
    } catch (err) {
      setTestDataError(await extractApiError(err));
    } finally {
      setTestDataLoading(false);
    }
  }, [customerId]);

  if (!customerId) {
    return (
      <div className="vmx-page">
        <div className="vmx-card">
          <div className="vmx-empty">
            <div className="vmx-empty-title">Invalid customer ID</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vmx-page">
      <div className="vmx-page-header">
        <div>
          <div className="vmx-page-title">
            <Link
              to="/admin"
              style={{
                color: "var(--vmx-accent)",
                textDecoration: "none",
                marginRight: 8,
              }}
            >
              ← Admin
            </Link>
            {customer?.name ?? customerId}
          </div>
          <div className="vmx-page-subtitle">
            Manage users and test data for this customer.
          </div>
        </div>
      </div>

      {error !== undefined && (
        <div
          className="vmx-card"
          style={{
            background: "var(--vmx-danger-bg, #fef2f2)",
            borderColor: "var(--vmx-danger)",
            color: "var(--vmx-danger)",
            padding: "12px 16px",
            marginBottom: 16,
            fontSize: "0.875rem",
          }}
        >
          {error}
        </div>
      )}

      {/* Assigned Users Section */}
      <div className="vmx-card" style={{ marginBottom: 24 }}>
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--vmx-gray-200)",
            fontWeight: 600,
            fontSize: "0.9375rem",
          }}
        >
          Assigned Users
        </div>
        {assignedUsers.length === 0 ? (
          <div style={{ padding: "24px 20px", color: "var(--vmx-gray-500)" }}>
            No users assigned to this customer yet.
          </div>
        ) : (
          <div className="vmx-table-wrapper">
            <table className="vmx-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignedUsers.map((user) => (
                  <tr key={user.email}>
                    <td data-label="Email">{user.email}</td>
                    <td data-label="Actions">
                      <button
                        className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                        style={{ color: "var(--vmx-danger)" }}
                        disabled={removingEmail === user.userId}
                        onClick={() => handleRemove(user.userId)}
                      >
                        {removingEmail === user.userId
                          ? "Removing..."
                          : "Remove"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Available Users Section */}
      <div className="vmx-card" style={{ marginBottom: 24 }}>
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--vmx-gray-200)",
            fontWeight: 600,
            fontSize: "0.9375rem",
          }}
        >
          Available Users
        </div>
        {availableUsers.length === 0 ? (
          <div style={{ padding: "24px 20px", color: "var(--vmx-gray-500)" }}>
            All users are already assigned to this customer.
          </div>
        ) : (
          <div className="vmx-table-wrapper">
            <table className="vmx-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Current Customer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {availableUsers.map((user) => (
                  <tr key={user.email}>
                    <td data-label="Email">{user.email}</td>
                    <td data-label="Current Customer">
                      {user.customerId ? user.customerId : "Unassigned"}
                    </td>
                    <td data-label="Actions">
                      <button
                        className="vmx-btn vmx-btn-primary vmx-btn-sm"
                        disabled={assigningEmail === user.email}
                        onClick={() => handleAssign(user)}
                      >
                        {assigningEmail === user.email
                          ? "Assigning..."
                          : "Assign"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Test Data Generation Section */}
      <div className="vmx-card">
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--vmx-gray-200)",
            fontWeight: 600,
            fontSize: "0.9375rem",
          }}
        >
          Test Data
        </div>
        <div style={{ padding: "20px" }}>
          <p
            style={{
              color: "var(--vmx-gray-600)",
              marginBottom: 16,
              fontSize: "0.875rem",
            }}
          >
            Generate sample certificate policies, device groups, and
            certificates for this customer. Existing test data will be replaced.
          </p>
          <button
            className="vmx-btn vmx-btn-primary"
            disabled={testDataLoading}
            onClick={handleGenerateTestData}
          >
            {testDataLoading ? "Generating..." : "Generate Test Data"}
          </button>

          {testDataError !== undefined && (
            <div
              style={{
                marginTop: 12,
                fontSize: "0.8125rem",
                color: "var(--vmx-danger)",
              }}
            >
              {testDataError}
            </div>
          )}

          {testDataSummary !== undefined && (
            <div
              style={{
                marginTop: 16,
                padding: "12px 16px",
                background: "var(--vmx-success-bg, #f0fdf4)",
                borderRadius: 6,
                fontSize: "0.875rem",
                color: "var(--vmx-gray-700)",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Test data generated successfully
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Certificate Policies: {testDataSummary.policiesCreated}</li>
                <li>Device Groups: {testDataSummary.deviceFamiliesCreated}</li>
                <li>Certificates: {testDataSummary.certificatesCreated}</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Reassignment Warning Modal */}
      {reassignTarget !== undefined && (
        <div
          className="vmx-modal-overlay"
          onClick={() => setReassignTarget(undefined)}
        >
          <div
            className="vmx-modal"
            style={{ maxWidth: 460 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vmx-modal-header">
              <div className="vmx-modal-title">Reassign User</div>
            </div>
            <div className="vmx-modal-body">
              <p style={{ color: "var(--vmx-gray-600)", lineHeight: 1.6 }}>
                <strong>{reassignTarget.email}</strong> is currently assigned to{" "}
                <strong>{reassignTarget.customerId}</strong>. Proceeding will
                move this user to{" "}
                <strong>{customer?.name ?? customerId}</strong>.
              </p>
            </div>
            <div className="vmx-modal-footer">
              <button
                className="vmx-btn vmx-btn-secondary"
                onClick={() => setReassignTarget(undefined)}
              >
                Cancel
              </button>
              <button
                className="vmx-btn vmx-btn-primary"
                onClick={handleConfirmReassign}
              >
                Confirm Reassignment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
